import React, { useCallback, useState } from "react";
import { Button, CircularProgress, Grid2 } from "@mui/material";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import { SenszClientServices } from "../../../ApiServices/http-common";
import {
  useWasteManagement,
  WasteManagementType,
} from "../../../ContextAPI/WasteManagementContext";
import dayjs from "dayjs";
import { showToast } from "../../../CommonComponents/ToastBox";
import { useTranslation } from "react-i18next";
import { FetchDLCReportExport } from "../../../ApiServices/ApiRequests";
import { useMasterData } from "../../../ContextAPI/MasterContext";
import { BinReportInfo } from "../../../Models/Models";
import Loading from "../../../Controller/LoadingController/Loading";
import AppColors from "../../../Utils/AppColor";

const ExportDLCReport: React.FC = () => {
  const { masterState } = useMasterData();
  const { wasteManagementState, dispatchWasteManagement } =
    useWasteManagement();
  const { t } = useTranslation();
  const [loading, setLoading] = useState<boolean>(false);

  const handleInputChange = useCallback(
    (
      field: keyof WasteManagementType,
      value: BinReportInfo[] | number | boolean
    ) => {
      dispatchWasteManagement({ type: "SET_FIELD", field, value });
    },
    [dispatchWasteManagement]
  );

  const handleExport = async (
    siteCode: string,
    fromDate: string,
    groupId: string
  ) => {
    if (wasteManagementState.siteData.length <= 0) {
      showToast("No data available to export.", "warning");
      return;
    }
    try {
      setLoading(true);
      const response: Blob = await FetchDLCReportExport(
        siteCode,
        fromDate,
        groupId
      );

      showToast("Data Exported.", "success");

      // Create a URL for the file download
      const url = window.URL.createObjectURL(
        new Blob([response], {
          type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        })
      );
      const currentDateAndTime: string = dayjs().format("YYYYMMDDHHmmss");
      const fileName: string = `bin_overview_report_${currentDateAndTime}.xlsx`;

      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", fileName); // Set the file name

      document.body.appendChild(link);
      link.click();

      // Clean up
      window.URL.revokeObjectURL(url);
      document.body.removeChild(link);
    } catch (error) {
      console.error("Dic Export Error");
    } finally {
      setLoading(false);
    }
  };

  const formattedDate: string = masterState.reportSelectedDate
    ? masterState.reportSelectedDate.format("YYYY-MM-DD")
    : "";

  return (
    <Grid2
      container
      justifyContent="flex-end" // Aligns items to the end of the grid
      alignItems="center"
      sx={{ padding: 0.5, mr: 1 }} // Optional: Add padding around the grid
    >
      <Button
        variant="contained"
        color="secondary"
        startIcon={
          loading ? (
            <CircularProgress sx={{ color: AppColors.appWhite }} size={30} />
          ) : (
            <FileDownloadIcon />
          )
        }
        disabled={loading}
        onClick={() =>
          handleExport(
            masterState.selectedSiteCode,
            formattedDate,
            masterState.selectedGroupCode
          )
        }
        style={{
          backgroundColor: "#8850EF", // Purple color similar to your image
          color: "#fff",
        }}
      >
        {loading ? t("global.Downloading") : t("global.Export")}
      </Button>
    </Grid2>
  );
};
export default ExportDLCReport;
